import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { PracticalInformationForm } from 'src/Components/Forms/PracticalInformation/PracticalInformationForm';
import { subtypeFieldTemplates } from 'src/Components/Forms/PracticalInformation/PracticalInformationForm/PracticalInformationCategoryMapping.helper';
import { RecurrenceValues } from 'src/Hooks/Forms/UseCreateEventForm';
import { transformRecurrenceDtoToFormValues } from 'src/Hooks/Forms/UseCreateEventForm/transformRecurrenceDtoToFormValues';
import { useCreatePracticalInformationForm } from 'src/Hooks/Forms/useCreatePracticalInformationForm';
import { useAddPracticalInformationAttachments } from 'src/Hooks/Mutations/useAddPracticalInformationAttachments';
import { useRemovePracticalInformationAttachment } from 'src/Hooks/Mutations/useRemovePracticalInformationAttachment';
import { useUpdatePracticalInformation } from 'src/Hooks/Mutations/useUpdatePracticalInformation';
import useFetchPracticalInformation from 'src/Hooks/Queries/useFetchPracticalInformation';
import {
  AssetStorageItemDtoTypeEnum,
  CreatePracticalInformationDtoSubtypeEnum,
  CreatePracticalInformationFieldDtoFieldTypeEnum,
  RecurrenceDto,
} from 'src/Services/API/generated';

import { Props } from './PracticalInformationEditionFormWrapper.types';

const PracticalInformationEditionFormWrapper = ({
  onCreate,
  onCancel,
  practicalInformationId,
}: Props) => {
  const { data: practicalInformation } = useFetchPracticalInformation(practicalInformationId);
  const form = useCreatePracticalInformationForm();
  const initialAttachmentFileIds = useRef<Set<string>>(new Set());
  const { mutate: addAttachments } = useAddPracticalInformationAttachments();
  const { mutate: removeAttachment } = useRemovePracticalInformationAttachment();
  const { mutate } = useUpdatePracticalInformation({
    onSuccess: () => {
      const currentFileIds = new Set(
        form.values.attachments.map(attachment => attachment.fileId).filter(Boolean)
      );

      for (const fileId of currentFileIds) {
        if (fileId && !initialAttachmentFileIds.current.has(fileId)) {
          addAttachments({ practicalInformationId, fileIds: [fileId] });
        }
      }

      for (const fileId of initialAttachmentFileIds.current) {
        if (!currentFileIds.has(fileId)) {
          removeAttachment({ practicalInformationId, attachmentId: fileId });
        }
      }

      onCreate();
    },
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (practicalInformation) {
      initialAttachmentFileIds.current = new Set(
        practicalInformation.attachments?.map(attachment => attachment.id) ?? []
      );

      form.setValues({
        title: practicalInformation.title,
        type: practicalInformation.type,
        subtype: practicalInformation.subtype,
        icon: practicalInformation.icon,
        reminderFieldKey: practicalInformation.reminderFieldKey,
        reminderDurationSeconds: practicalInformation.reminderDurationSeconds,
        reminderTimeOfDay: practicalInformation.reminderTimeOfDay,
        fields: practicalInformation.fields.map(field => {
          const fieldType =
            subtypeFieldTemplates[
              practicalInformation.subtype as CreatePracticalInformationDtoSubtypeEnum
            ]?.find(template => template.key === field.key)?.fieldType ??
            CreatePracticalInformationFieldDtoFieldTypeEnum.Text;

          let value: string | string[] | RecurrenceValues | Date | undefined;

          if (fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Date) {
            value = field.value ? dayjs(field.value as unknown as string).toDate() : undefined;
          } else if (fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule) {
            value = transformRecurrenceDtoToFormValues(field.value as RecurrenceDto);
          } else {
            value = field.value as string | string[] | RecurrenceValues | Date;
          }

          return {
            value: value,
            key: field.key,
            fieldType: fieldType,
          };
        }),
        attachments: (practicalInformation.attachments ?? []).map(attachment => ({
          fileId: attachment.id,
          name: attachment.name,
          previewUrl: attachment.urls?.previewThumbnailUrl,
          type: AssetStorageItemDtoTypeEnum.File,
        })),
      });
    }
  }, [practicalInformation]);

  return (
    <PracticalInformationForm
      onCancel={onCancel}
      form={form}
      onSubmit={form.onSubmit(values => {
        mutate({ practicalInformationId, data: values });
      })}
      submitButtonLabel={t('createEventForm.editEvent', 'Modifier')}
    />
  );
};

export default PracticalInformationEditionFormWrapper;
